import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as Styled from '../../common/CommonStyledComponents';
import { Button, FullColoredButton } from '../../../common/buttonsComponents';
import { FormColumn, MainFormColumn } from '../../../common/policyStyles';
import { shopifyUpdateOrderState } from '../../../../actions/tools/ShopifyActions';
import { oneBtnPopupCreator, twoBtnPopupCreator } from '../../../popups/ConfirmPopup/helper';
import Loading from '../../../LoadingComponent/LoadingComponent';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { setSelectedTask } from '../../../../actions/tools/OrdersSearchActions';
import { addNoteToSelectedTicket } from '../../../../actions';
import { extractSelectedInboxTab, extractSelectedTicketFromState, isSpecialist } from '../../../../extractFromState';
import { getSelectedShop, selectSelectedToolDomains, getSelectedOrder } from '../../../../selectors/tools';
import ShopifyPolicy from '../ShopifyPolicy/ShopifyPolicy';
import BigNumber from 'bignumber.js';
import { Label, Input, InputWrapper, FormRowAlignNone } from './ShopifyIssueRefundAmount.styles';
import { handleError } from 'services/ErrorService/ErrorService';
import { shopifyRefundAmount } from '../../../../api/orders/shopify';

class ShopifyIssueRefundAmount extends Component {
  static propTypes = {
    selectedOrder: PropTypes.object.isRequired,
    setSelectedTask: PropTypes.func,
    shopifyUpdateOrderState: PropTypes.func.isRequired,
    addNoteToSelectedTicket: PropTypes.func,
    selectedInboxTab: PropTypes.string,
  };

  static defaultProps = {
    setSelectedTask: () => {},
    addNoteToSelectedTicket: () => Promise.resolve(),
  };

  state = {
    options: [],
    amount: '',
    reasonComment: '',
    isRequesting: false,
  };

  get shopifyConfig() {
    const configs = this.props.selectedToolDomains['shopifyConfigs'] || {};

    return configs[this.props.selectedShop] || {};
  }

  get refundConfig() {
    return this.shopifyConfig.refundConfig || {};
  }

  get refundPolicy() {
    return this.refundConfig.refundPolicy || {};
  }

  get amount() {
    return BigNumber(this.state.amount).toNumber();
  }

  get createdDate() {
    return (this.props.selectedOrder || {}).created_at || '';
  }

  handleBackBtnClick = () => {
    this.props.setSelectedTask(null);
  };

  get orderId() {
    return String(this.props.selectedOrder.id);
  }

  static displayPopupErrorMessage(message = '') {
    oneBtnPopupCreator({
      content: {
        main: message,
        iconPath: '/images/error-esclamation.svg',
      },
      btnConfig: 'CLOSE',
    });
  }

  isRefundLimitExceeded() {
    const amountNumber = parseFloat(this.state.amount);

    return this.refundLimitPrice !== 0 ? this.refundLimitPrice <= amountNumber : false;
  }

  get refundLimitDays() {
    const refundPolicy = this.refundConfig.refundPolicy || {};

    return typeof refundPolicy.days === 'undefined' ? null : refundPolicy.days;
  }

  get refundLimitPrice() {
    const maxPriceAllowed = this.refundPolicy['maxPriceAllowed'];

    return typeof maxPriceAllowed === 'undefined' ? 0 : parseInt(maxPriceAllowed);
  }

  get placeholderText() {
    return 'The customer has paid for this order. You can refund a customer a given dollar amount.';
  }

  handleRefundBtnClick = () => {
    if (!BigNumber(this.amount).decimalPlaces(2).isEqualTo(this.amount) || this.amount <= 0) {
      return ShopifyIssueRefundAmount.displayPopupErrorMessage('Please enter correct amount for refund');
    }

    if (!this.state.reasonComment.trim()) {
      return ShopifyIssueRefundAmount.displayPopupErrorMessage('Please add reason for refund amount');
    }

    if (this.isRefundLimitExceeded()) {
      return ShopifyIssueRefundAmount.displayPopupErrorMessage(`Partner limits for refund $${this.refundLimitPrice}`);
    }

    twoBtnPopupCreator({
      content: {
        main: 'Are you sure you want to refund amount?',
        iconPath: '/images/green-alert-bell.svg',
      },
      btnConfig: {
        dismiss: 'DISMISS',
        confirm: 'MANUAL REFUND',
      },
      onConfirm: this.confirmationHandler,
    });
  };

  handlerTextInputChange = ({ target: { name, value } }) => {
    this.setState({ [name]: value });
  };

  async updateSelectedOrder() {
    this.setState({ isUpdateOrderRequest: true });
    try {
      await this.props.shopifyUpdateOrderState(this.orderId, this.props.selectedShop);
    } catch (error) {
      handleError({ error });
    }
    this.setState({ isUpdateOrderRequest: false });
  }

  getErrorMessage(err) {
    return (err.response && err.response.text) || '';
  }

  confirmationHandler = async () => {
    this.setState({ isRequesting: true });

    const payload = {
      amount: this.amount,
      comment: this.state.reasonComment,
      orderId: this.orderId,
      conversationId: this.props.selectedTicket.ticketId,
      addNotes: true,
      shopName: this.props.selectedShop,
    };

    try {
      await shopifyRefundAmount(payload);
    } catch (error) {
      handleError({ error });

      return this.handleFailedRefund(this.getErrorMessage(error));
    }

    await this.updateSelectedOrder();
    await this.handleSuccessRefund();
  };

  get orderName() {
    const { selectedOrder } = this.props;

    return (selectedOrder && selectedOrder.name.replace('#', '')) || this.orderId;
  }

  handleSuccessRefund = async () => {
    this.setState({ isRequesting: false });
    this.props.setSelectedTask(null);

    oneBtnPopupCreator({
      content: {
        main: `Refund order successful: Refunded $${this.amount.toFixed(2)}`,
        iconPath: '/images/checkmark-green.svg',
      },
      btnConfig: 'CLOSE',
    });
  };

  handleFailedRefund = (message) => {
    this.setState({ isRequesting: false });

    oneBtnPopupCreator({
      content: {
        main: 'Refund failed',
        subText: [message],
        iconPath: '/images/error-esclamation.svg',
      },
      btnConfig: 'CLOSE',
    });
  };

  get isSpecialist() {
    return this.props.isSpecialist;
  }

  render() {
    if (this.state.isRequesting) {
      return <Loading label="Sent request..." />;
    }

    return (
      <ShopifyPolicy
        createdDate={this.createdDate}
        showCancel={false}
        showRefund={true}
        config={this.shopifyConfig}
        isSpecialist={this.isSpecialist}
      >
        <Styled.OrderTaskWrapper>
          <Styled.OrderTaskTitle>Refund a Given Dollar Amount</Styled.OrderTaskTitle>
          <Styled.OrderTaskDescription>{this.placeholderText}</Styled.OrderTaskDescription>
          <FormRowAlignNone>
            <MainFormColumn>
              <Styled.StepTitle>
                <Styled.Subtext>Step 1 - </Styled.Subtext>
                Please enter amount for refund <Styled.Required />
              </Styled.StepTitle>

              <InputWrapper>
                <Label>Amount:</Label>
                <Input
                  autocomplete="off"
                  value={this.state.amount}
                  name="amount"
                  type="text"
                  onChange={this.handlerTextInputChange}
                />
              </InputWrapper>
            </MainFormColumn>
            <MainFormColumn>
              <Styled.StepTitle>
                <Styled.Subtext>Step 2 - </Styled.Subtext>
                Select reason for refunding <Styled.Required />
              </Styled.StepTitle>

              <FormColumn>
                <Styled.RefundCommentTextarea
                  name="reasonComment"
                  value={this.state.reasonComment}
                  style={{ minHeight: '80px', resize: 'vertical', marginTop: '10px' }}
                  onChange={this.handlerTextInputChange}
                />
              </FormColumn>
            </MainFormColumn>
          </FormRowAlignNone>
          <Styled.OrderTaskControls>
            <Button onClick={this.handleBackBtnClick}>BACK</Button>
            <FullColoredButton active={true} onClick={this.handleRefundBtnClick}>
              REFUND AMOUNT
            </FullColoredButton>
          </Styled.OrderTaskControls>
        </Styled.OrderTaskWrapper>
      </ShopifyPolicy>
    );
  }
}

const mapStateToProps = (state) => ({
  selectedOrder: getSelectedOrder(state),
  selectedTicket: extractSelectedTicketFromState(state),
  selectedInboxTab: extractSelectedInboxTab(state),
  selectedShop: getSelectedShop(state),
  selectedToolDomains: selectSelectedToolDomains(state),
  isSpecialist: isSpecialist(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setSelectedTask,
      addNoteToSelectedTicket,
      shopifyUpdateOrderState,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ShopifyIssueRefundAmount);
